import { Mail } from "@/assets";
import { getRecaptchaSiteKey } from "@/constants/appConstants";
import { sprWithJobIdFlow } from "@/constants/common";
import { useRegistrationProps } from "@/context/useRegistrationProps";
import { useOnLoadTracking } from "@/hooks";
import useRecaptchaV3 from "@/hooks/useRecaptchaV3";
import { FormValues } from "@/types";
import { Details, UserProfile } from "@/types/userDetails";
import { getCookie } from "@/utils/cookies";
import {
  INDIAN_PHONE_NUMBER_REGEX,
  NON_INDIAN_PHONE_NUMBER_REGEX,
  emailRegex,
} from "@/utils/regex";
import { getSiteProps } from "@/utils/siteProps";
import { Input, MobileTextInput } from "@monsterindia/global-ui-components";
import React, { Suspense, lazy, useMemo, useRef, useState } from "react";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";

import ContentWrapper from "../../common/ContentWrapper";
import SocialLogin from "./social-login";
import "./style.css";
import TermsAndConditions from "./termsAndCondition";
import WhatsAppNotify from "./whatsapp";

const CheckExist = lazy(() => import("./modals/check-exist"));
// const SocialLogin = lazy(() => import("./social-login"));

const emailRequiredForSites = [
  "monstergulf",
  "monsterthailand",
  "monstervietnam",
  "monsterindonesia",
  "monsterhongkong",
];

const SignUp: React.FC = () => {
  useOnLoadTracking();
  const {
    translation,
    details,
    setDetails,
    setIsLoading,
    handleOnSubmit,
    isApp,
    flowType,
    hostname,
  } = useRegistrationProps();
  const siteConfig = getSiteProps(hostname);

  const queryString = window.location.search;
  const params = new URLSearchParams(queryString);
  const splSource = params.get("spl");
  const mfjSource = params.get("mfjSource");
  const [socialLoginUser, setSocialLoginUser] = useState<UserProfile | null>(
    null,
  );
  const existUserRef = useRef<any>(null);
  const { executeRecaptcha } = useRecaptchaV3(
    getRecaptchaSiteKey(hostname),
    true,
    false,
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    clearErrors,
    trigger,
    getValues,
    register,
  } = useForm<FormValues>({
    defaultValues: {
      email: "",
      countryCode: siteConfig?.country_isd_code
        ? `+${siteConfig.country_isd_code}`
        : "+91",
      mobileNumber: "",
      whatsappOptedIn: true,
    },
    mode: "onBlur",
  });

  const email = useWatch({ control, name: "email" });
  const countryCode = useWatch({ control, name: "countryCode" });
  const mobileNumber = useWatch({ control, name: "mobileNumber" });
  const mobileRegex =
    countryCode === "+91"
      ? INDIAN_PHONE_NUMBER_REGEX
      : NON_INDIAN_PHONE_NUMBER_REGEX;

  const validationRules = useMemo(
    () => ({
      email: {
        required: translation.errorMessages.fieldRequired,
        pattern: {
          value: emailRegex,
          message: translation.socialLogin.emailNotValid,
        },
      },
      mobileNumber: {
        required: translation.errorMessages.fieldRequired,
        pattern: {
          value: mobileRegex,
          message: translation.socialLogin.validNumber,
        },
      },
    }),
    [countryCode],
  );

  const handleUserExist = async (
    field: "email" | "mobileNumber",
    value: string,
  ) => {
    const isValid = await trigger(field);
    if (isValid) {
      if (field === "email") {
        return await existUserRef.current?.checkExistUser(field, value);
      } else {
        return await existUserRef.current?.checkExistUser(field, {
          countryCode: countryCode.slice(1),
          mobileNumber: value,
        });
      }
    }
    return false;
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setIsLoading(true);
    const { reCaptchaToken, reCaptchaAction } =
      await executeRecaptcha("addnumber");

    setDetails((prev: Details) => ({ ...prev, ...data }));
    // const emailOtpCountries = ["+92", "+60"];
    const isMobileNumberClaimed =
      existUserRef?.current?.checkMobileNumberClaimed();

    /* if mobile number is claimed
      then do not check email and mobile number existence
      */
    if (!isMobileNumberClaimed) {
      const isEmailExist = await handleUserExist("email", data.email);
      if (isEmailExist) {
        setIsLoading(false);
        return;
      }

      const isMobileNumberExist = await handleUserExist(
        "mobileNumber",
        data.mobileNumber,
      );

      if (isMobileNumberExist && !isMobileNumberClaimed) {
        setIsLoading(false);
        return;
      }
    }

    /* otpData is exactly same as details.opt and details.otpId
      this was added as details state (in useContext) is not setting synchronously
    */
    const otpData = existUserRef?.current?.otpData;

    handleOnSubmit(
      {
        email: data.email,
        mobileNumber: {
          countryCode: data.countryCode.slice(1),
          mobileNumber: data.mobileNumber,
          ...(details.otp &&
            details.otpId && { otp: details.otp, otpId: details.otpId }),
          ...(otpData.otp &&
            otpData.otpId && { otp: otpData.otp, otpId: otpData.otpId }),
        },
        reCaptchaToken,
        reCaptchaAction,
        whatsappOptedIn: data.whatsappOptedIn,
        source_name: "CONVERSATIONAL_REG",
        ...(socialLoginUser?.accessToken && {
          socialAccessToken: socialLoginUser.accessToken,
          socialProvider: socialLoginUser.provider,
        }),
        ...(splSource && { splSource }),
        ...(mfjSource && { mfjSource }),
      },
      (details && details?.otp) || (otpData && otpData?.otp)
        ? "RESUME"
        : siteConfig?.site_map === "rexmonster"
          ? "MOBILE_OTP"
          : "EMAIL_OTP",
      {
        "Submitted_Mobile_No._&_Email": `${data.countryCode}${data.mobileNumber}, ${data.email}`,
        source_name: "CONVERSATIONAL_REG",
        splEventProps: {
          countryCode: data.countryCode.slice(1),
          mobileNumber: {
            countryCode: data.countryCode.slice(1),
            mobileNumber: data.mobileNumber,
            ...(details.otp &&
              details.otpId && { otp: details.otp, otpId: details.otpId }),
            ...(otpData.otp &&
              otpData.otpId && { otp: otpData.otp, otpId: otpData.otpId }),
          },
          email: data.email,
          whatsappOptedIn: data.whatsappOptedIn,
          referrer: getCookie("ref"),
        },
      },
    );
  };
  //   [executeRecaptcha, handleOnSubmit, details, setDetails, socialLoginUser],
  // );

  return (
    <form className="h-full" onSubmit={handleSubmit(onSubmit)}>
      <ContentWrapper
        className="flex-grow gap-6"
        text={
          flowType === sprWithJobIdFlow
            ? translation.socialLogin.applyJob
            : !socialLoginUser
              ? translation.socialLogin.title
              : translation.socialLogin.recruiterTitle
        }
      >
        {!socialLoginUser && !isApp && (
          // <Suspense
          //   fallback={<div className="h-8 animate-pulse rounded bg-gray-100" />}
          // >
          <SocialLogin
            handleAfterSocialLogin={(user) => {
              setValue("email", user.email ?? "");
              setSocialLoginUser(user);
            }}
          />
          // </Suspense>
        )}

        <div className="flex flex-col gap-3">
          {!socialLoginUser && (
            <Controller
              name="email"
              control={control}
              rules={validationRules.email}
              render={({ field }) => {
                return (
                  <Input
                    {...field}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      e.target.value = e.target.value.trim();
                      field.onChange(e.target.value);
                      field.onBlur();
                    }}
                    id="email"
                    placeholder={translation.socialLogin.email}
                    errorText={errors.email?.message}
                    leftIcon={<Mail height={16} width={16} />}
                  />
                );
              }}
            />
            // <Input
            //   {...register("email", {
            //     required: translation.errorMessages.fieldRequired,
            //     pattern: {
            //       value: emailRegex,
            //       message: translation.socialLogin.emailNotValid,
            //     },
            //   })}
            //   id="email"
            //   placeholder={translation.socialLogin.email}
            //   errorText={errors.email?.message}
            //   leftIcon={<Mail height={16} width={16} />}
            // />
          )}
          <Controller
            name="mobileNumber"
            control={control}
            rules={validationRules.mobileNumber}
            render={({ field }) => (
              <MobileTextInput
                id="mobileNumber"
                placeholder={translation.socialLogin.mobilePlaceholder}
                mobileNumber={field.value ?? ""}
                setMobileNumber={(value: string) => {
                  clearErrors("mobileNumber");
                  setValue("mobileNumber", value);
                }}
                countryCode={{
                  text: getValues("countryCode")
                    ? getValues("countryCode")
                    : siteConfig?.country_isd_code
                      ? `+${siteConfig.country_isd_code}`
                      : "+91",
                }}
                setCountryCode={(value: { text: string }) =>
                  setValue("countryCode", value.text)
                }
                dropdownDisabled={
                  siteConfig?.site_map === "rexmonster" ? true : false
                }
                errorText={errors.mobileNumber?.message}
              />
            )}
          />
        </div>
        <WhatsAppNotify
          updateToggle={(value: boolean) => setValue("whatsappOptedIn", value)}
        />
        <TermsAndConditions />
        <Suspense fallback={null}>
          <CheckExist
            ref={existUserRef}
            payload={{ countryCode: countryCode.slice(1), mobileNumber, email }}
          />
        </Suspense>
      </ContentWrapper>
    </form>
  );
};

export default SignUp;
